(() => {
  if (customElements.get('orion-image-viewer')) return;

  class ImagePanSurface {
    constructor(surface, plane, options = {}) {
      this.surface = surface; this.plane = plane; this.options = options;
      this.scale = 1; this.x = 0; this.y = 0; this.points = new Map();
      this.abort = new AbortController();
      const eventOptions = { signal: this.abort.signal, capture: true };
      surface.addEventListener('pointerdown', event => {
        if (this.options.isEnabled?.() === false || event.button !== 0 || event.target.closest('button, a, orion-roll-pack-carton')) return;
        event.stopImmediatePropagation();
        this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
        surface.setPointerCapture?.(event.pointerId);
        this.startGesture();
      }, eventOptions);
      surface.addEventListener('pointermove', event => {
        if (this.options.isEnabled?.() !== false && this.points.has(event.pointerId)) {
          event.stopImmediatePropagation();
          this.points.set(event.pointerId, { x: event.clientX, y: event.clientY });
          const points = [...this.points.values()];
          if (points.length >= 2 && this.pinch && this.options.zoom !== false) {
            const midpoint = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
            const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
            const scale = this.limit(this.pinch.scale * distance / Math.max(1, this.pinch.distance), 1, 4);
            const rect = surface.getBoundingClientRect();
            const ratio = scale / this.pinch.scale;
            this.set(scale,
              this.pinch.x + midpoint.x - this.pinch.midpoint.x + (1 - ratio) * (this.pinch.midpoint.x - rect.left - rect.width / 2 - this.pinch.x),
              this.pinch.y + midpoint.y - this.pinch.midpoint.y + (1 - ratio) * (this.pinch.midpoint.y - rect.top - rect.height / 2 - this.pinch.y));
          } else if (points.length === 1 && this.drag && this.scale > 1.01) {
            this.set(this.scale, this.drag.x + event.clientX - this.drag.point.x, this.drag.y + event.clientY - this.drag.point.y);
          }
        }
      }, eventOptions);
      const finish = event => {
        if (!this.points.has(event.pointerId)) return;
        event.stopImmediatePropagation();
        const drag = this.drag, wasSingle = this.points.size === 1;
        this.points.delete(event.pointerId);
        if (event.type === 'pointerup' && wasSingle && drag && !drag.pinched && this.scale <= 1.01) {
          const dx = event.clientX - drag.point.x, dy = event.clientY - drag.point.y;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3 && performance.now() - drag.time < 1600) this.options.onSwipe?.(dx < 0 ? 1 : -1);
        }
        this.startGesture();
      };
      surface.addEventListener('pointerup', finish, eventOptions);
      surface.addEventListener('pointercancel', finish, eventOptions);
      // zoom: false (a set of scenes) keeps only the swipe.
      surface.addEventListener('dblclick', event => {
        if (this.options.isEnabled?.() === false || this.options.zoom === false || event.target.closest('button, a, orion-roll-pack-carton')) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.zoom(this.scale > 1.05 ? 1 : 2.2, event.clientX, event.clientY, true);
      }, eventOptions);
      surface.addEventListener('wheel', event => {
        if (this.options.isEnabled?.() === false || this.options.zoom === false || event.target.closest('button, a, orion-roll-pack-carton')) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.zoom(this.scale * Math.exp(-event.deltaY * .0015), event.clientX, event.clientY);
      }, { ...eventOptions, passive: false });
      this.lastSize = { width: surface.clientWidth, height: surface.clientHeight };
      this.resize = new ResizeObserver(() => {
        const width = surface.clientWidth, height = surface.clientHeight;
        if (width === this.lastSize.width && height === this.lastSize.height) return;
        this.lastSize = { width, height }; this.set(this.scale, this.x, this.y);
      });
      this.resize.observe(surface);
      this.set(1, 0, 0);
    }
    limit(value, min, max) { return Math.max(min, Math.min(max, value)); }
    startGesture() {
      this.settleAnimation();
      const points = [...this.points.values()];
      if (points.length >= 2) {
        this.pinch = { scale: this.scale, x: this.x, y: this.y, distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), midpoint: { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } };
        if (this.drag) this.drag.pinched = true;
      } else {
        const pinched = !!this.pinch;
        this.pinch = null;
        this.drag = points[0] ? { point: points[0], x: this.x, y: this.y, time: performance.now(), pinched } : null;
      }
    }
    bounds(scale) {
      const image = this.plane.querySelector('img');
      return { x: this.plane.clientWidth * (scale - 1) / 2, y: Math.max(0, (Math.min(image?.clientHeight || this.plane.clientHeight, this.plane.clientHeight) * scale - this.plane.clientHeight) / 2) };
    }
    set(scale, x, y, smooth = false) {
      const previous = this.animation?.playState === 'running' ? getComputedStyle(this.plane).transform : this.plane.style.transform || 'translate3d(0,0,0) scale(1)';
      this.scale = this.limit(scale, 1, 4);
      const bounds = this.bounds(this.scale);
      this.x = this.limit(x, -bounds.x, bounds.x); this.y = this.limit(y, -bounds.y, bounds.y);
      const transform = 'translate3d(' + this.x + 'px,' + this.y + 'px,0) scale(' + this.scale + ')';
      this.animation?.cancel();
      this.plane.style.transform = transform;
      this.options.onChange?.(this.scale);
      if (smooth && (this.options.motion || !matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        this.animation = this.plane.animate([{ transform: previous }, { transform }], { duration: 340, easing: 'cubic-bezier(.2,.7,.2,1)' });
        return this.animation.finished.catch(() => {});
      }
      return Promise.resolve();
    }
    settleAnimation() {
      if (this.animation?.playState !== 'running') return;
      const matrix = new DOMMatrixReadOnly(getComputedStyle(this.plane).transform);
      this.animation.cancel(); this.animation = null;
      this.scale = matrix.a; this.x = matrix.e; this.y = matrix.f;
      this.plane.style.transform = 'translate3d(' + this.x + 'px,' + this.y + 'px,0) scale(' + this.scale + ')';
    }
    zoom(scale, clientX, clientY, smooth = false) {
      this.settleAnimation();
      scale = this.limit(scale, 1, 4);
      const rect = this.surface.getBoundingClientRect(), ratio = scale / this.scale;
      const localX = (clientX ?? rect.left + rect.width / 2) - rect.left - rect.width / 2;
      const localY = (clientY ?? rect.top + rect.height / 2) - rect.top - rect.height / 2;
      return this.set(scale, this.x + (1 - ratio) * (localX - this.x), this.y + (1 - ratio) * (localY - this.y), smooth);
    }
    reset(smooth = false) {
      if (this.scale === 1 && this.x === 0 && this.y === 0 && this.animation?.playState !== 'running') return Promise.resolve();
      return this.set(1, 0, 0, smooth);
    }
    destroy(reset = true) { this.abort.abort(); this.resize.disconnect(); this.animation?.cancel(); this.points.clear(); if (reset) this.plane.style.transform = ''; }
  }

  class OrionImageViewer extends HTMLElement {
    attach(gallery) {
      if (this.gallery) return;
      this.gallery = gallery;
      this.abort = new AbortController();
      this.busy = false; this.token = 0;
      this.dialog = this.querySelector('[data-viewer-dialog]').content.firstElementChild.cloneNode(true);
      document.body.appendChild(this.dialog);
      this.stage = this.dialog.querySelector('[data-viewer-stage]');
      // The refreshed page: on a phone the full-screen view is never smaller than the page (orion-mattress-viewer.css).
      this.dialog.toggleAttribute('data-fill', !!gallery.closest('.orion-mattress-product--refresh'));
      const signal = this.abort.signal;
      this.dialog.addEventListener('click', event => {
        const select = event.target.closest('[data-viewer-select]');
        const step = event.target.closest('[data-viewer-step]');
        const scale = event.target.closest('[data-viewer-scale]');
        if (event.target.closest('[data-viewer-close]')) this.closeView();
        else if (this.busy) return;
        else if (select) this.select(Number(select.dataset.viewerSelect));
        else if (step) this.step(Number(step.dataset.viewerStep));
        else if (scale) this.pan?.zoom(this.pan.scale + Number(scale.dataset.viewerScale), undefined, undefined, true);
        else if (event.target.closest('[data-viewer-reset]')) this.pan?.reset(true);
      }, { signal });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeView(); }, { signal });
      window.addEventListener('resize', () => { if (this.dialog.open && this.scene && !this.busy) this.frameDetail(this.scene, this.index); }, { signal });
      this.dialog.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || this.busy) return;
        const direction = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
        if (direction) {
          event.preventDefault();
          if (this.pan?.scale > 1.01) this.pan.set(this.pan.scale, this.pan.x + (event.key === 'ArrowLeft' ? 60 : event.key === 'ArrowRight' ? -60 : 0), this.pan.y + (event.key === 'ArrowUp' ? 60 : event.key === 'ArrowDown' ? -60 : 0));
          else this.step(direction);
        } else if (event.key === '+' || event.key === '=') { event.preventDefault(); this.pan?.zoom(this.pan.scale + .5, undefined, undefined, true); }
        else if (event.key === '-') { event.preventDefault(); this.pan?.zoom(this.pan.scale - .5, undefined, undefined, true); }
        else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          if (this.set) { this.selectEntry(event.key === 'Home' ? 0 : this.set.scenes.length - 1); return; }
          const indices = this.gallery.usableIndices(); this.select(event.key === 'Home' ? indices[0] : indices[indices.length - 1]);
        }
      }, { signal });
      this.syncAvailability();
    }

    motionDuration() { return this.gallery._reducedMotion.matches && !this.gallery._allowMotion ? 0 : 680; }
    syncAvailability() {
      if (!this.gallery) return;
      const button = this.gallery.querySelector('[data-gallery-zoom]');
      button.setAttribute('aria-label', this.dataset.expandLabel);
      button.setAttribute('aria-expanded', String(this.dialog.open));
      button.setAttribute('aria-haspopup', 'dialog');
    }
    async waitForGallery() {
      const start = performance.now();
      while ((this.gallery._moving || this.gallery.hasAttribute('aria-busy')) && performance.now() - start < 16000) await new Promise(resolve => setTimeout(resolve, 32));
      return this.gallery.isConnected && this.gallery._shown && !this.gallery._moving;
    }

    // origin (optional): { index, image, clip, returnFocus } opens a gallery view from an image elsewhere on the
    // page (e.g. the construction figure below the gallery); the zoom grows out of that image and returns to it.
    // With figure: true instead of an index it opens that image itself, alone (a figure the gallery does not hold),
    // with kind (the scene's look), fullSrc (a sharper copy to load once open) and tone: 'light' (a light viewer, for
    // a figure from a light block of the page).
    // With scenes instead, it opens a set of scenes of its own (openSet).
    async openView(origin) {
      if (origin?.scenes) return this.openSet(origin);
      if (!this.gallery._shown || this.busy || this.dialog.open) return;
      this.set = null;
      this.busy = true; this.closeRequested = false;
      const operation = ++this.token;
      this.gallery.suspendDemo(true);
      const ready = await this.waitForGallery();
      if (operation !== this.token || !this.gallery) return;
      if (!ready) { this.busy = false; this.gallery.suspendDemo(false); return; }
      this.origin = origin?.image && (origin.figure || this.gallery.usableIndices().includes(origin.index)) ? origin : null;
      this.index = this.origin ? (this.origin.figure ? -1 : this.origin.index) : this.gallery._index;
      this.returnFocus = this.origin?.returnFocus || this.gallery.querySelector('[data-gallery-zoom]');
      const from = this.origin
        ? this.captureImage(this.origin.image, this.origin.clip || this.origin.image)
        : this.captureImage(this.gallery._views[this.index].querySelector('img'), this.gallery.querySelector('.orion-mattress-gallery__viewport'));
      this.scene = this.createScene(this.index);
      // A gallery view that has not been shown yet has no loaded image: start from the visible origin image.
      const sceneImage = this.origin && this.scene.querySelector('img');
      if (sceneImage && !sceneImage.getAttribute('src')) sceneImage.src = this.origin.image.currentSrc || this.origin.image.src;
      this.stage.replaceChildren(this.scene);
      this.updateNavigation();
      this.savedOverflow = document.documentElement.style.overflow;
      this.savedGutter = document.documentElement.style.scrollbarGutter;
      document.documentElement.style.scrollbarGutter = 'stable';
      document.documentElement.style.overflow = 'hidden';
      // The picture itself goes into the viewer: its place stays empty while it is open, as the gallery's does.
      if (this.index >= 0) this.gallery.setAttribute('data-viewer-expanded', '');
      else this.origin.image.style.visibility = 'hidden';
      if (this.origin?.tone) this.dialog.dataset.tone = this.origin.tone;
      this.dialog.showModal();
      this.dialog.querySelector('[data-viewer-close]').focus({ preventScroll: true });
      this.frameDetail(this.scene, this.index);
      this.syncAvailability();
      const duration = this.motionDuration();
      const shade = this.dialog.querySelector('[data-viewer-shade]');
      const animations = duration ? [
        shade.animate([{ opacity: 0 }, { opacity: 1 }], { duration, easing: 'ease' }),
        ...[...this.dialog.querySelectorAll('[data-viewer-chrome]')].map(el => el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: duration * .8, delay: duration * .18, fill: 'backwards' }))
      ] : [];
      if (this.origin?.tone) this.paperFilter(shade);
      // The light viewer's paper reaches the page's scrollbar gutter too, which shows the root's own background.
      if (this.origin?.tone) this.gutter = document.documentElement.animate(
        [{ backgroundColor: getComputedStyle(document.documentElement).backgroundColor }, { backgroundColor: getComputedStyle(shade).backgroundColor }],
        { duration: duration || 1, easing: 'ease', fill: 'forwards' }
      );
      await Promise.all([this.morph(from), ...animations.map(a => a.finished.catch(() => {}))]);
      if (operation !== this.token || !this.dialog.open) return;
      this.busy = false;
      if (this.closeRequested) { this.closeRequested = false; this.closeView(); return; }
      this.attachPan();
      this.upgradeImage(this.scene, this.index);
    }

    // A set of scenes of its own (the fact tiles of the refreshed mattress page, assets/orion-mattress-facts.js):
    // { scenes, at }, where each scene is a gallery view ({ view: index }) or built content ({ build() }), with its tile,
    // label and value (the header shows them), decorate(scene) as it is made and enter(scene) once it is on screen.
    // The set opens out of the tile of the scene at `at` and closes into the tile of the scene then shown, with the
    // gallery zoom's duration and easing; its own numbers and steps move through it. Nothing zooms in a set.
    async openSet(set) {
      if (!this.gallery._shown || this.busy || this.dialog.open || !set.scenes.length) return;
      this.busy = true; this.closeRequested = false;
      const operation = ++this.token;
      this.gallery.suspendDemo(true);
      const ready = await this.waitForGallery();
      if (operation !== this.token || !this.gallery) return;
      if (!ready) { this.busy = false; this.gallery.suspendDemo(false); return; }
      this.set = set; this.origin = null;
      this.position = Math.max(0, Math.min(set.scenes.length - 1, set.at || 0));
      const entry = set.scenes[this.position];
      await this.prepareEntry(entry);
      if (operation !== this.token || !this.gallery) return;
      this.index = entry.view ?? -1;
      this.returnFocus = entry.tile;
      this.scene = this.createEntryScene(entry);
      this.stage.replaceChildren(this.scene);
      this.dialog.setAttribute('data-set', '');
      this.headerText(entry);
      this.updateNavigation();
      this.savedOverflow = document.documentElement.style.overflow;
      this.savedGutter = document.documentElement.style.scrollbarGutter;
      document.documentElement.style.scrollbarGutter = 'stable';
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.dialog.querySelector('[data-viewer-close]').focus({ preventScroll: true });
      this.frameDetail(this.scene, this.index);
      entry.enter?.(this.scene);
      this.syncAvailability();
      const duration = this.motionDuration();
      const shade = this.dialog.querySelector('[data-viewer-shade]');
      const animations = duration ? [
        shade.animate([{ opacity: 0 }, { opacity: 1 }], { duration, easing: 'ease' }),
        ...[...this.dialog.querySelectorAll('[data-viewer-chrome]')].map(el => el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: duration * .8, delay: duration * .18, fill: 'backwards' }))
      ] : [];
      await Promise.all([this.grow(this.scene, entry.tile), ...animations.map(a => a.finished.catch(() => {}))]);
      if (operation !== this.token || !this.dialog.open) return;
      this.busy = false;
      if (this.closeRequested) { this.closeRequested = false; this.closeView(); return; }
      this.attachPan();
      if (this.index >= 0) this.upgradeImage(this.scene, this.index);
    }
    // A gallery view of a set is shown with its picture loaded, as the gallery shows it.
    async prepareEntry(entry) {
      if (entry.view != null) await this.gallery.load(entry.view).catch(() => {});
    }
    createEntryScene(entry) {
      let scene;
      if (entry.view != null) scene = this.createScene(entry.view);
      else {
        scene = document.createElement('div');
        scene.className = 'orion-mattress-gallery orion-image-viewer__scene';
        scene.dataset.kind = 'fact';
        const viewport = document.createElement('div'), view = document.createElement('div');
        viewport.className = 'orion-mattress-gallery__viewport';
        view.className = 'orion-mattress-gallery__view orion-mattress-gallery__view--fact is-current';
        view.appendChild(entry.build());
        viewport.appendChild(view); scene.appendChild(viewport);
      }
      scene.dataset.fact = entry.kind;
      entry.decorate?.(scene);
      return scene;
    }
    // The header names the scene of a set (its label and value); closing gives it back to the product.
    headerText(entry) {
      const eyebrow = this.dialog.querySelector('.orion-image-viewer__eyebrow'), title = this.dialog.querySelector('.orion-image-viewer__title');
      if (!eyebrow || !title) return;
      if (entry) {
        this.savedHeader ??= [eyebrow.textContent, title.textContent];
        eyebrow.textContent = entry.label || ''; title.textContent = entry.value || '';
      } else if (this.savedHeader) {
        [eyebrow.textContent, title.textContent] = this.savedHeader;
        this.savedHeader = null;
      }
    }
    // A scene of a set grows out of its tile, as the tile itself opening (scaled about the tile's centre and cut to its
    // rounded shape), and goes back into it on closing.
    grow(scene, tile, reverse = false) {
      const duration = this.motionDuration();
      if (!duration || !scene || !tile?.isConnected) return Promise.resolve();
      const box = scene.getBoundingClientRect(), from = tile.getBoundingClientRect();
      if (!box.width || !box.height || !from.width || !from.height) return Promise.resolve();
      const scale = Math.max(from.width / box.width, from.height / box.height);
      const x = from.left + from.width / 2 - box.left, y = from.top + from.height / 2 - box.top;
      const w = from.width / scale / 2, h = from.height / scale / 2;
      const radius = parseFloat(getComputedStyle(tile).borderTopLeftRadius) || 0;
      const origin = x + 'px ' + y + 'px';
      const small = { transformOrigin: origin, transform: 'scale(' + scale + ')', clipPath: 'inset(' + [y - h, box.width - x - w, box.height - y - h, x - w].map(value => value + 'px').join(' ') + ' round ' + radius / scale + 'px)', opacity: 0 };
      const full = { transformOrigin: origin, transform: 'scale(1)', clipPath: 'inset(0px 0px 0px 0px round 0px)', opacity: 1 };
      const frames = reverse ? [full, { offset: .7, opacity: 1 }, small] : [small, { offset: .3, opacity: 1 }, full];
      return scene.animate(frames, { duration, easing: 'cubic-bezier(.22,.7,.2,1)', fill: reverse ? 'forwards' : 'none' }).finished.catch(() => {});
    }
    async selectEntry(position, direction) {
      if (this.busy || !this.set || position === this.position || position < 0 || position >= this.set.scenes.length) return;
      this.busy = true;
      const operation = ++this.token;
      const entry = this.set.scenes[position];
      this.dialog.setAttribute('aria-busy', 'true');
      await this.prepareEntry(entry);
      if (operation !== this.token || !this.dialog.open) return;
      this.dialog.removeAttribute('aria-busy');
      direction = direction || (position > this.position ? 1 : -1);
      await this.pan?.reset(true);
      if (operation !== this.token || !this.dialog.open) return;
      this.pan?.destroy(); this.pan = null;
      const old = this.scene, next = this.createEntryScene(entry);
      this.stage.appendChild(next);
      this.index = entry.view ?? -1;
      this.frameDetail(next, this.index);
      entry.enter?.(next);
      const duration = this.motionDuration() ? 600 : 0;
      if (duration) {
        const options = { duration, easing: 'cubic-bezier(.3,0,.2,1)', fill: 'both' };
        const animations = [
          old.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(' + direction * -13 + '%)', opacity: 0 }], options),
          next.animate([{ transform: 'translateY(' + direction * 13 + '%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], options)
        ];
        await Promise.all(animations.map(a => a.finished.catch(() => {})));
        animations.forEach(a => a.cancel());
      }
      if (operation !== this.token || !this.dialog.open) return;
      old.remove(); this.scene = next; this.position = position;
      this.returnFocus = entry.tile;
      this.headerText(entry);
      this.updateNavigation(); this.attachPan();
      if (this.index >= 0) this.upgradeImage(next, this.index);
      this.dialog.querySelector('[data-viewer-status]').textContent = [entry.label, entry.value].filter(Boolean).join(' ');
      this.busy = false;
      if (this.closeRequested) { this.closeRequested = false; this.closeView(); }
    }
    async closeSet(focus = true) {
      if (!this.dialog.open || this.closing) return;
      if (this.busy && !this.dialog.hasAttribute('aria-busy')) { this.closeRequested = true; return; }
      this.closeRequested = false;
      this.closing = true; this.busy = true; ++this.token;
      this.dialog.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      this.dialog.removeAttribute('aria-busy');
      await this.pan?.reset(true);
      this.pan?.destroy(); this.pan = null;
      const entry = this.set.scenes[this.position];
      const duration = this.motionDuration();
      const animations = duration ? [
        this.dialog.querySelector('[data-viewer-shade]').animate([{ opacity: 1 }, { opacity: 0 }], { duration, fill: 'forwards' }),
        ...[...this.dialog.querySelectorAll('[data-viewer-chrome]')].map(el => el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: duration * .35, fill: 'forwards' }))
      ] : [];
      await Promise.all([this.grow(this.scene, entry.tile, true), ...animations.map(a => a.finished.catch(() => {}))]);
      this.dialog.close();
      animations.forEach(a => a.cancel());
      this.dialog.removeAttribute('data-set');
      this.headerText(null);
      document.documentElement.style.overflow = this.savedOverflow || '';
      document.documentElement.style.scrollbarGutter = this.savedGutter || '';
      this.stage.replaceChildren(); this.scene = null; this.set = null; this.busy = false; this.closing = false;
      this.syncAvailability();
      if (focus) entry.tile?.focus({ preventScroll: true });
      this.gallery.suspendDemo(false);
    }

    // A detail view is a crop of the photograph. In full screen show only that crop, exactly as the gallery frames it,
    // enlarged and centred (re-deriving it from the wider full-screen proportions reveals the whole mattress). The rest
    // of the photograph is masked and the crop's edges cut it sharp, as the gallery's frame does; no light is drawn
    // behind it (assets/orion-mattress-viewer.css). Falls back to the stylesheet framing when the gallery view has no layout.
    frameDetail(scene, index) {
      if (!scene || scene.dataset.kind !== 'detail') return;
      const view = this.gallery._views[index];
      const source = view?.querySelector('img');
      const stage = scene.querySelector('[data-image-plane]');
      const target = stage?.querySelector('img');
      if (!source || !target) return;
      // The stage's layout size, not its rectangle, which a zoom in progress would scale.
      const v = view.getBoundingClientRect(), r = source.getBoundingClientRect(), s = { width: stage.offsetWidth, height: stage.offsetHeight };
      if (![v.width, v.height, r.width, r.height, s.width, s.height].every(value => value > 0)) return;
      // The gallery's crop as fractions of the image box; like the gallery, it may reach past the photograph.
      const crop = [(v.left - r.left) / r.width, (v.top - r.top) / r.height, (v.right - r.left) / r.width, (v.bottom - r.top) / r.height];
      const [x0, y0, x1, y1] = crop, fw = x1 - x0, fh = y1 - y0, ratio = r.width / r.height;
      // The whole stage on a phone of the refreshed page (data-fill), a little air round it elsewhere.
      const fill = this.dialog.hasAttribute('data-fill') && window.matchMedia('(max-width: 749px)').matches ? 1 : .94;
      const width = Math.min(s.width * fill / fw, s.height * fill * ratio / fh), height = width / ratio;
      const left = s.width / 2 - (x0 + fw / 2) * width, top = s.height / 2 - (y0 + fh / 2) * height;
      Object.assign(target.style, {
        position: 'absolute', maxWidth: 'none', maxHeight: 'none', transform: 'none',
        width: width + 'px', height: height + 'px', left: left + 'px', top: top + 'px'
      });
      target.dataset.detailCrop = crop.join(' ');
      // The crop's box on the stage; the contact shadow keeps its place in it, as in the gallery.
      const box = [left + x0 * width, top + y0 * height, fw * width, fh * height];
      const shadow = view.querySelector('.orion-mattress-gallery__contact'), contact = stage.querySelector('.orion-mattress-gallery__contact');
      if (shadow?.offsetParent && contact) Object.assign(contact.style, {
        left: box[0] + shadow.offsetLeft / v.width * box[2] + 'px', right: 'auto', top: box[1] + shadow.offsetTop / v.height * box[3] + 'px',
        width: shadow.offsetWidth / v.width * box[2] + 'px', height: shadow.offsetHeight / v.height * box[3] + 'px'
      });
      // The mask sits on the plane, so it follows every zoom and pan.
      this.maskBox(stage, box);
    }

    // A figure printed on the paper of the page (mix-blend-mode: multiply) moves printed the same way: this filter
    // multiplies it by the colour of the light viewer's paper, so its white is the paper while it travels.
    paperFilter(shade) {
      const [r, g, b] = getComputedStyle(shade).backgroundColor.match(/[\d.]+/g).map(Number);
      if (!document.getElementById('OrionViewerPaper')) document.body.insertAdjacentHTML('beforeend', '<svg width="0" height="0" aria-hidden="true" style="position:absolute"><filter id="OrionViewerPaper" color-interpolation-filters="sRGB"><feColorMatrix type="matrix"/></filter></svg>');
      document.querySelector('#OrionViewerPaper feColorMatrix').setAttribute('values', [r, g, b].map((value, row) => [0, 1, 2, 3, 4].map(column => column === row ? (value / 255).toFixed(4) : 0).join(' ')).join(' ') + ' 0 0 0 1 0');
    }

    // Shows only a box of an element (in its own coordinates), cut sharp on every side.
    maskBox(element, [left, top, width, height]) {
      const mask = { Image: 'linear-gradient(#000,#000)', Size: width + 'px ' + height + 'px', Position: left + 'px ' + top + 'px', Repeat: 'no-repeat' };
      for (const [name, value] of Object.entries(mask)) { element.style['webkitMask' + name] = value; element.style['mask' + name] = value; }
    }

    createScene(index) {
      const scene = document.createElement('div');
      scene.className = 'orion-mattress-gallery orion-image-viewer__scene';
      if (index < 0) return this.createFigureScene(scene);
      scene.dataset.kind = this.gallery._views[index].dataset.kind || 'image';
      const light = this.gallery.querySelector('.orion-mattress-gallery__light').cloneNode(true);
      const viewport = document.createElement('div');
      viewport.className = 'orion-mattress-gallery__viewport';
      const view = this.gallery._views[index].cloneNode(true);
      // The full view keeps the still picture: a memory pillow's press film stays on the page (snippets/orion-memory-film).
      view.querySelectorAll('video').forEach(film => film.remove());
      view.classList.remove('is-moving'); view.classList.add('is-current');
      view.setAttribute('aria-hidden', 'false');
      view.removeAttribute('data-view');
      const plane = view.querySelector('[data-image-plane]');
      plane.style.transform = '';
      const image = view.querySelector('img'), original = this.gallery._views[index].querySelector('img');
      image.removeAttribute('srcset');
      image.src = original.currentSrc || original.src;
      viewport.appendChild(view); scene.append(light, viewport);
      return scene;
    }
    // The scene of a figure the gallery does not hold: the image alone, built like a gallery view, with no light.
    createFigureScene(scene) {
      const source = this.origin.image;
      scene.dataset.kind = this.origin.kind || 'figure';
      const viewport = document.createElement('div'), view = document.createElement('div');
      const plane = document.createElement('div'), image = document.createElement('img');
      viewport.className = 'orion-mattress-gallery__viewport';
      view.className = 'orion-mattress-gallery__view orion-mattress-gallery__view--figure is-current';
      plane.className = 'orion-mattress-gallery__plane';
      plane.setAttribute('data-image-plane', '');
      image.className = 'orion-mattress-gallery__image';
      image.alt = source.alt || ''; image.draggable = false;
      image.width = source.naturalWidth || Number(source.getAttribute('width')) || 0;
      image.height = source.naturalHeight || Number(source.getAttribute('height')) || 0;
      image.src = source.currentSrc || source.src;
      plane.appendChild(image); view.appendChild(plane); viewport.appendChild(view); scene.appendChild(viewport);
      return scene;
    }
    attachPan() {
      this.pan?.destroy();
      // A built scene of a set has no picture plane: its content takes the swipe, and nothing zooms in a set.
      const plane = this.scene.querySelector('[data-image-plane]') || this.scene.querySelector('.orion-mattress-gallery__view');
      this.pan = new ImagePanSurface(this.stage, plane, {
        motion: this.gallery._allowMotion,
        zoom: !this.set,
        isEnabled: () => !this.busy,
        onSwipe: direction => { if (!this.busy) this.step(direction); },
        onChange: scale => {
          this.dialog.toggleAttribute('data-scaled', scale > 1.01);
          this.dialog.querySelector('[data-viewer-reset]').textContent = Number(scale.toFixed(1)) + '×';
        }
      });
    }
    captureImage(image, viewport) {
      const box = image.getBoundingClientRect(), style = getComputedStyle(image);
      const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || box.width;
      const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || box.height;
      const fit = Math.min(box.width / Math.max(1, naturalWidth), box.height / Math.max(1, naturalHeight));
      const width = naturalWidth * fit, height = naturalHeight * fit;
      const position = style.objectPosition.split(' ');
      const fraction = value => value?.endsWith('%') ? parseFloat(value) / 100 : .5;
      return {
        left: box.left + (box.width - width) * fraction(position[0]),
        top: box.top + (box.height - height) * fraction(position[1]),
        width, height, clip: viewport.getBoundingClientRect(),
        src: image.currentSrc || image.src, filter: style.filter
      };
    }
    async morph(from, reverse = false) {
      const duration = this.motionDuration();
      if (!duration || !this.scene || !from) return;
      const scene = this.scene;
      const full = this.captureImage(scene.querySelector('img'), scene.querySelector('.orion-mattress-gallery__viewport'));
      const start = reverse ? full : from, end = reverse ? from : full;
      if (![start.width, start.height, end.width, end.height].every(value => Number.isFinite(value) && value > 0)) return;
      const origin = this.dialog.getBoundingClientRect();
      const layer = document.createElement('div'), image = document.createElement('img');
      layer.className = 'orion-image-viewer__motion';
      layer.setAttribute('aria-hidden', 'true');
      // A live view (the Roll Pack carton, drawn in 3D) travels as the frame it shows at this moment.
      image.alt = ''; image.src = scene.querySelector('[data-live-frame]')?.captureFrame?.() || full.src; image.draggable = false;
      image.className = 'orion-image-viewer__motion-image';
      Object.assign(image.style, {
        left: end.left - origin.left + 'px', top: end.top - origin.top + 'px',
        width: end.width + 'px', height: end.height + 'px', filter: full.filter
      });
      // A detail view keeps its crop through the zoom: the gallery's crop grows into the full-screen one and back.
      const sceneImage = scene.querySelector('img'), crop = sceneImage.dataset.detailCrop?.split(' ').map(Number);
      if (crop?.length === 4) {
        const box = sceneImage.getBoundingClientRect();
        const x = f => (box.left + f * box.width - full.left) / full.width * end.width;
        const y = f => (box.top + f * box.height - full.top) / full.height * end.height;
        this.maskBox(image, [x(crop[0]), y(crop[1]), x(crop[2]) - x(crop[0]), y(crop[3]) - y(crop[1])]);
      }
      layer.appendChild(image); this.dialog.appendChild(layer);
      scene.setAttribute('data-viewer-morphing', '');
      const clip = box => 'inset(' + [
        Math.max(0, box.top - origin.top), Math.max(0, origin.right - box.right),
        Math.max(0, origin.bottom - box.bottom), Math.max(0, box.left - origin.left)
      ].map(value => value + 'px').join(' ') + ')';
      const options = { duration, easing: 'cubic-bezier(.22,.7,.2,1)', fill: 'both' };
      const scale = start.width / end.width;
      const animations = [
        image.animate([
          { transform: 'translate3d(' + (start.left - end.left) + 'px,' + (start.top - end.top) + 'px,0) scale(' + scale + ')' },
          { transform: 'translate3d(0,0,0) scale(1)' }
        ], options),
        layer.animate([{ clipPath: clip(start.clip) }, { clipPath: clip(end.clip) }], options),
        scene.animate(reverse ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }], options)
      ];
      const cleanup = () => {
        animations.forEach(animation => animation.cancel());
        layer.remove(); scene.removeAttribute('data-viewer-morphing');
        if (this.motionCleanup === cleanup) this.motionCleanup = null;
      };
      this.motionCleanup = cleanup;
      await Promise.all(animations.map(animation => animation.finished.catch(() => {})));
      if (!reverse) cleanup();
    }

    updateNavigation() {
      if (this.set) {
        const scenes = this.set.scenes, count = scenes.length, numbers = this.dialog.querySelector('[data-viewer-numbers]');
        numbers.replaceChildren(...scenes.map((entry, rank) => {
          const button = document.createElement('button');
          button.type = 'button'; button.dataset.viewerSelect = String(rank);
          button.setAttribute('aria-label', entry.label || String(rank + 1));
          if (rank === this.position) button.setAttribute('aria-current', 'true');
          button.textContent = String(rank + 1).padStart(2, '0');
          return button;
        }));
        numbers.hidden = count < 2;
        this.dialog.querySelectorAll('[data-viewer-step]').forEach(button => {
          button.hidden = count < 2;
          button.setAttribute('aria-label', scenes[(this.position + Number(button.dataset.viewerStep) + count) % count].label || '');
        });
        this.dialog.querySelector('[data-viewer-count]').textContent = String(this.position + 1).padStart(2, '0') + ' / ' + String(count).padStart(2, '0');
        return;
      }
      const indices = this.index < 0 ? [] : this.gallery.usableIndices(), position = indices.indexOf(this.index);
      const numbers = this.dialog.querySelector('[data-viewer-numbers]');
      numbers.replaceChildren();
      indices.forEach((index, rank) => {
        const label = this.gallery.dataset.loadLabel.replace('__INDEX__', String(rank + 1));
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.viewerSelect = String(index);
        button.setAttribute('aria-label', label);
        if (index === this.index) button.setAttribute('aria-current', 'true');
        button.textContent = String(rank + 1).padStart(2, '0'); numbers.appendChild(button);

      });
      numbers.hidden = indices.length < 2;
      this.dialog.querySelectorAll('[data-viewer-step]').forEach(button => {
        button.hidden = indices.length < 2;
        const rank = (position + Number(button.dataset.viewerStep) + indices.length) % indices.length;
        button.setAttribute('aria-label', this.gallery.dataset.loadLabel.replace('__INDEX__', String(rank + 1)));
      });
      this.dialog.querySelector('[data-viewer-count]').textContent = indices.length ? String(position + 1).padStart(2, '0') + ' / ' + String(indices.length).padStart(2, '0') : '';
    }
    step(direction) {
      if (this.set) {
        const count = this.set.scenes.length;
        if (!this.busy && count > 1) this.selectEntry((this.position + direction + count) % count, direction);
        return;
      }
      const indices = this.gallery.usableIndices();
      if (this.busy || this.index < 0 || indices.length < 2) return;
      this.select(indices[(indices.indexOf(this.index) + direction + indices.length) % indices.length], direction);
    }
    async select(index, direction) {
      if (this.set) return this.selectEntry(index, direction);
      if (this.busy || this.index < 0 || index === this.index || !this.gallery.usableIndices().includes(index)) return;
      this.busy = true;
      const operation = ++this.token;
      this.dialog.setAttribute('aria-busy', 'true');
      try { await this.gallery.load(index); }
      catch (_) {
        if (operation !== this.token || !this.dialog.open) return;
        this.gallery._failed.add(index); this.gallery.syncControls(); this.updateNavigation();
        this.dialog.querySelector('[data-viewer-status]').textContent = this.gallery.dataset.errorLabel;
        this.dialog.removeAttribute('aria-busy'); this.busy = false; return;
      }
      if (operation !== this.token || !this.dialog.open) return;
      this.dialog.removeAttribute('aria-busy');
      direction = direction || (index > this.index ? 1 : -1);
      await this.pan?.reset(true);
      if (operation !== this.token || !this.dialog.open) return;
      this.pan?.destroy();
      const old = this.scene, next = this.createScene(index);
      this.stage.appendChild(next);
      this.frameDetail(next, index);
      const duration = this.motionDuration() ? 600 : 0;
      if (duration) {
        const options = { duration, easing: 'cubic-bezier(.3,0,.2,1)', fill: 'both' };
        const animations = [
          old.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(' + direction * -13 + '%)', opacity: 0 }], options),
          next.animate([{ transform: 'translateY(' + direction * 13 + '%)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }], options)
        ];
        await Promise.all(animations.map(a => a.finished.catch(() => {})));
        animations.forEach(a => a.cancel());
      }
      if (operation !== this.token || !this.dialog.open) return;
      old.remove(); this.scene = next; this.index = index;
      this.updateNavigation(); this.attachPan(); this.upgradeImage(next, index);
      this.dialog.querySelector('[data-viewer-status]').textContent = this.gallery._views[index].getAttribute('aria-label');
      this.busy = false;
      if (this.closeRequested) { this.closeRequested = false; this.closeView(); }
    }

    upgradeImage(scene, index) {
      const original = this.gallery._views[index]?.querySelector('img');
      let href = this.origin?.figure ? this.origin.fullSrc : '';
      if (original) {
        const url = new URL(original.dataset.fullSrc, location.href);
        // At most data-zoom-max wide where the gallery says so (the materials: 2048 px).
        const own = original.getAttribute('width'), most = Number(this.gallery.dataset.zoomMax);
        url.searchParams.set('width', most && Number(own) > most ? String(most) : own);
        href = url.href;
      }
      if (!href) return;
      const upgrade = new Image(); upgrade.src = href;
      upgrade.decode().then(() => { if (scene.isConnected && this.dialog.open) scene.querySelector('img').src = upgrade.src; }).catch(() => {});
    }

    async closeView(focus = true) {
      if (this.set) return this.closeSet(focus);
      if (!this.dialog.open || this.closing) return;
      if (this.busy && !this.dialog.hasAttribute('aria-busy')) { this.closeRequested = true; return; }
      this.closeRequested = false;
      this.closing = true; this.busy = true; ++this.token;
      this.dialog.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      this.dialog.removeAttribute('aria-busy');
      await this.pan?.reset(true);
      this.pan?.destroy(); this.pan = null;
      // Opened from an image outside the gallery and still on that view: return to that image, leave the gallery alone.
      const toOrigin = this.origin && (this.index < 0 || this.index === this.origin.index) && this.origin.image.isConnected;
      if (!toOrigin && this.index >= 0) await this.gallery.goTo(this.index, undefined, true);
      const destination = toOrigin
        ? this.captureImage(this.origin.image, this.origin.clip || this.origin.image)
        : this.index >= 0 ? this.captureImage(this.gallery._views[this.index].querySelector('img'), this.gallery.querySelector('.orion-mattress-gallery__viewport')) : null;
      const duration = this.motionDuration();
      const animations = duration ? [
        this.dialog.querySelector('[data-viewer-shade]').animate([{ opacity: 1 }, { opacity: 0 }], { duration, fill: 'forwards' }),
        ...[...this.dialog.querySelectorAll('[data-viewer-chrome]')].map(el => el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: duration * .35, fill: 'forwards' }))
      ] : [];
      if (this.gutter) { this.gutter.reverse(); animations.push(this.gutter); this.gutter = null; }
      await Promise.all([this.morph(destination, true), ...animations.map(a => a.finished.catch(() => {}))]);
      this.dialog.close();
      delete this.dialog.dataset.tone;
      if (this.origin?.figure) this.origin.image.style.visibility = '';
      animations.forEach(a => a.cancel()); this.motionCleanup?.();
      this.gallery.removeAttribute('data-viewer-expanded');
      document.documentElement.style.overflow = this.savedOverflow || '';
      document.documentElement.style.scrollbarGutter = this.savedGutter || '';
      this.stage.replaceChildren(); this.scene = null; this.busy = false; this.closing = false;
      this.syncAvailability();
      if (focus) this.returnFocus?.focus({ preventScroll: true });
      this.origin = null;
      this.gallery.suspendDemo(false);
    }
    teardown() {
      if (!this.gallery) return;
      this.abort.abort(); this.token++;
      this.gutter?.cancel(); this.gutter = null;
      if (this.origin?.figure) this.origin.image.style.visibility = '';
      this.motionCleanup?.();
      this.gallery.removeAttribute('data-viewer-expanded');
      this.pan?.destroy();
      if (this.set) { this.headerText(null); this.dialog.removeAttribute('data-set'); this.set = null; }
      if (this.dialog.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.savedOverflow || '';
        document.documentElement.style.scrollbarGutter = this.savedGutter || '';
      }
      this.dialog.remove();
      this.gallery = null;
    }
    disconnectedCallback() { this.teardown(); }
  }
  customElements.define('orion-image-viewer', OrionImageViewer);
})();
